import { useCallback, useEffect, useRef, useState } from "react";
import { Platform, Pressable, ScrollView, Text, TextInput, View, type ViewStyle } from "react-native";

import { FONTS, RADII, STROKES } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import { useAuth } from "../../../lib/authContext";
import {
  runSqlConsole,
  SQL_CONSOLE_EXAMPLES,
  type SqlConsoleResult,
} from "../../../lib/adminSqlConsole";
import { AdminButton, AdminEmpty, AdminPageHeader } from "../../../components/admin/ui";

const THEME = PANE_THEMES.Admin;

// Generic monospace family for the query editor + result cells. Not a brand
// font — a code surface needs a fixed-width family; "monospace" is the CSS
// generic keyword, not a specific typeface.
const MONO = Platform.OS === "web" ? "ui-monospace, Menlo, Consolas, monospace" : "monospace";

export default function AdminSqlConsoleScreen() {
  const { role } = useAuth();
  const isSuperAdmin = role === "super_admin";

  const [sql, setSql] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<SqlConsoleResult | null>(null);
  const inputRef = useRef<TextInput | null>(null);

  const run = useCallback(async () => {
    if (!sql.trim() || running) return;
    setRunning(true);
    try {
      const r = await runSqlConsole(sql);
      setResult(r);
    } finally {
      setRunning(false);
    }
  }, [sql, running]);

  // Cmd/Ctrl-Enter to run (web). Attach to the underlying textarea node.
  useEffect(() => {
    if (Platform.OS !== "web") return;
    const node = inputRef.current as unknown as HTMLTextAreaElement | null;
    if (!node) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        void run();
      }
    };
    node.addEventListener("keydown", onKey);
    return () => node.removeEventListener("keydown", onKey);
  }, [run]);

  if (!isSuperAdmin) {
    return (
      <View style={{ flex: 1, backgroundColor: THEME.pageBg, padding: 24 }}>
        <AdminEmpty
          theme={THEME}
          title="Super admin only"
          body="The SQL console is restricted to super admins."
        />
      </View>
    );
  }

  return (
    <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }} contentContainerStyle={{ padding: 24, gap: 18 }}>
      <AdminPageHeader
        theme={THEME}
        eyebrow="Super admin"
        title="SQL console"
        subtitle="Run ad-hoc read-only queries against the database."
        actions={<ReadOnlyBadge />}
      />

      {/* Example starters */}
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
        {SQL_CONSOLE_EXAMPLES.map((ex) => (
          <Pressable
            key={ex.label}
            onPress={() => setSql(ex.sql)}
            style={[
              {
                paddingHorizontal: 10,
                paddingVertical: 6,
                borderRadius: RADII.full,
                borderWidth: STROKES.hairline,
                borderColor: THEME.border,
                backgroundColor: THEME.surfaceBg,
              } as ViewStyle,
              Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11.5, color: THEME.textOnSurfaceDim }}>
              {ex.label}
            </Text>
          </Pressable>
        ))}
      </View>

      {/* Editor */}
      <View
        style={{
          borderWidth: STROKES.hairline,
          borderColor: THEME.border,
          borderRadius: RADII.md,
          backgroundColor: THEME.surfaceBg,
          overflow: "hidden",
        }}
      >
        <TextInput
          ref={inputRef}
          value={sql}
          onChangeText={setSql}
          placeholder="select * from competitions limit 20"
          placeholderTextColor={THEME.textOnSurfaceFaint}
          multiline
          autoCapitalize="none"
          autoCorrect={false}
          style={[
            {
              minHeight: 150,
              padding: 14,
              fontFamily: MONO,
              fontSize: 13,
              lineHeight: 20,
              color: THEME.textOnSurface,
            },
            Platform.OS === "web" ? ({ outlineStyle: "none" } as unknown as object) : null,
          ]}
        />
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            paddingHorizontal: 14,
            paddingVertical: 10,
            borderTopWidth: STROKES.hairline,
            borderTopColor: THEME.border,
          }}
        >
          <Text style={{ fontFamily: FONTS.body, fontSize: 11, color: THEME.textOnSurfaceFaint }}>
            SELECT / WITH / EXPLAIN only · ⌘/Ctrl-Enter to run · ≤ 1,000 rows · 8s limit
          </Text>
          <AdminButton
            theme={THEME}
            label={running ? "Running…" : "Run"}
            onPress={() => void run()}
            loading={running}
            disabled={!sql.trim()}
            size="sm"
          />
        </View>
      </View>

      {/* Results */}
      {result ? <ResultPanel result={result} /> : null}
    </ScrollView>
  );
}

function ReadOnlyBadge() {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: RADII.full,
        borderWidth: STROKES.hairline,
        borderColor: THEME.successBorderSoft,
        backgroundColor: THEME.successTintSoft,
      }}
    >
      <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: THEME.success }} />
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: THEME.success,
        }}
      >
        Read-only
      </Text>
    </View>
  );
}

function ResultPanel({ result }: { result: SqlConsoleResult }) {
  if (!result.ok) {
    return (
      <View
        style={{
          padding: 14,
          borderRadius: RADII.md,
          borderWidth: STROKES.hairline,
          borderColor: THEME.border,
          backgroundColor: THEME.surfaceBg,
          gap: 4,
        }}
      >
        <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 10, letterSpacing: 1.4, textTransform: "uppercase", color: THEME.destructive }}>
          Query failed
        </Text>
        <Text style={{ fontFamily: MONO, fontSize: 12.5, color: THEME.textOnSurface }}>
          {result.error}
        </Text>
      </View>
    );
  }

  return (
    <View style={{ gap: 8 }}>
      <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: THEME.textOnSurfaceDim }}>
        {result.rowCount} {result.rowCount === 1 ? "row" : "rows"}
        {result.truncated ? " (capped at 1,000)" : ""} · {result.durationMs} ms
        {result.explain ? " · query plan" : ""}
      </Text>
      {result.rows.length === 0 ? (
        <View
          style={{
            padding: 14,
            borderRadius: RADII.md,
            borderWidth: STROKES.hairline,
            borderColor: THEME.border,
            backgroundColor: THEME.surfaceBg,
          }}
        >
          <Text style={{ fontFamily: FONTS.body, fontSize: 12.5, color: THEME.textOnSurfaceFaint }}>
            No rows returned.
          </Text>
        </View>
      ) : (
        <ResultTable columns={result.columns} rows={result.rows} />
      )}
    </View>
  );
}

function ResultTable({ columns, rows }: { columns: string[]; rows: Record<string, unknown>[] }) {
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator
      style={{
        borderWidth: STROKES.hairline,
        borderColor: THEME.border,
        borderRadius: RADII.md,
        backgroundColor: THEME.surfaceBg,
      }}
    >
      <View>
        {/* Header */}
        <View style={{ flexDirection: "row", backgroundColor: THEME.surfaceRaisedBg, borderBottomWidth: STROKES.hairline, borderBottomColor: THEME.border }}>
          {columns.map((c) => (
            <HeaderCell key={c} label={c} />
          ))}
        </View>
        {/* Rows */}
        {rows.map((row, i) => (
          <View
            key={i}
            style={{
              flexDirection: "row",
              borderBottomWidth: i === rows.length - 1 ? 0 : STROKES.hairline,
              borderBottomColor: THEME.divider,
            }}
          >
            {columns.map((c) => (
              <DataCell key={c} value={row[c]} />
            ))}
          </View>
        ))}
      </View>
    </ScrollView>
  );
}

const CELL_WIDTH = 200;

function HeaderCell({ label }: { label: string }) {
  return (
    <View style={{ width: CELL_WIDTH, paddingHorizontal: 12, paddingVertical: 9 }}>
      <Text
        numberOfLines={1}
        style={{ fontFamily: FONTS.bodyBold, fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", color: THEME.textOnSurfaceDim }}
      >
        {label}
      </Text>
    </View>
  );
}

function DataCell({ value }: { value: unknown }) {
  const isNull = value === null || value === undefined;
  const text = isNull
    ? "NULL"
    : typeof value === "object"
      ? JSON.stringify(value)
      : String(value);
  return (
    <View style={{ width: CELL_WIDTH, paddingHorizontal: 12, paddingVertical: 8 }}>
      <Text
        numberOfLines={2}
        style={{
          fontFamily: MONO,
          fontSize: 12,
          color: isNull ? THEME.textOnSurfaceFaint : THEME.textOnSurface,
        }}
      >
        {text}
      </Text>
    </View>
  );
}
